import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Plus, MoreVertical, Edit, Trash2, Package, Search, Loader2, Truck, Upload, FileSpreadsheet, Download, CheckCircle, XCircle, ClipboardList, Save, AlertTriangle, Barcode, Image as ImageIcon, ShoppingBag, Tag, Globe, ExternalLink, ArrowRightLeft, DollarSign, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { LabelPrintDialog } from '../components/LabelPrintDialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Inventory = () => {
  const { getAuthHeader, activeStaff, hasPermission, user } = useAuth();
  const { selectedStore, storeName } = useStore();
  const isAdmin = !activeStaff || hasPermission('admin');
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('all');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  // Bulk selection state
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showBulkEditSupplier, setShowBulkEditSupplier] = useState(false);
  const [labelDialogProducts, setLabelDialogProducts] = useState(null);
  const [bulkSupplierName, setBulkSupplierName] = useState('');
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [removingDuplicates, setRemovingDuplicates] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    barcode: '',
    supplier_code: '',
    brand: '',
    supplier_id: '',
    supplier_name: '',
    quantity: 0,
    buy_price: '',
    sell_price: '',
    tax_rate: 15,
    category: 'General',
    description: '',
    image_url: ''
  });
  
  // Generate barcode state
  const [generateBarcode, setGenerateBarcode] = useState(false);
  
  // Publish to Shopify state
  const [publishToShopify, setPublishToShopify] = useState(false);
  const [publishingToShopify, setPublishingToShopify] = useState(false);
  
  // Restore stock state
  const [restoreLoading, setRestoreLoading] = useState(false);
  
  // Image upload state
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);
  
  // Bulk Import state
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [uploadingEdits, setUploadingEdits] = useState(false);
  const bulkEditInputRef = useRef(null);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // Stock Count state
  const [isStockCountOpen, setIsStockCountOpen] = useState(false);
  const [stockCountStore, setStockCountStore] = useState('orewa');
  const [stockCountItems, setStockCountItems] = useState([]);
  const [stockCountLoading, setStockCountLoading] = useState(false);
  const [stockCountSearch, setStockCountSearch] = useState('');
  const [stockCountBrand, setStockCountBrand] = useState('all');
  const [stockCountSupplier, setStockCountSupplier] = useState('all');

  // Nutriscript import state
  const [isNutriscriptDialogOpen, setIsNutriscriptDialogOpen] = useState(false);
  const [nutriscriptLoading, setNutriscriptLoading] = useState(false);
  const [nutriscriptStatus, setNutriscriptStatus] = useState(null);
  const nutriscriptPollRef = useRef(null);

  // Shopify Bulk Publish state
  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false);
  const [shopifyCollections, setShopifyCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState('');
  const [publishPrices, setPublishPrices] = useState({});
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const [collectionsLoading, setCollectionsLoading] = useState(false);

  // Category filter tab
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Transfer to Royal Oak state
  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false);
  const [transferQuantities, setTransferQuantities] = useState({});
  const [transferring, setTransferring] = useState(false);
  const [transferResult, setTransferResult] = useState(null);

  // Price List Update state
  const [isPriceListOpen, setIsPriceListOpen] = useState(false);
  const [priceListFile, setPriceListFile] = useState(null);
  const [priceListLoading, setPriceListLoading] = useState(false);
  const [priceListResult, setPriceListResult] = useState(null);
  const [priceListApplying, setPriceListApplying] = useState(false);
  const priceListInputRef = useRef(null);

  const [page, setPage] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  const pageSize = 100;

  const fetchData = async (pageNum = 0, serverSearch = '', catFilter = '') => {
    setLoading(true);
    try {
      const noBarcode = catFilter === 'nobarcode';
      const searchParam = serverSearch ? `&search=${encodeURIComponent(serverSearch)}` : '';
      const categoryParam = (catFilter && !noBarcode) ? `&category=${encodeURIComponent(catFilter)}` : '';
      const noBarcodeParam = noBarcode ? '&no_barcode=true' : '';
      const [productsRes, suppliersRes] = await Promise.all([
        axios.get(`${API_URL}/products?limit=${pageSize}&skip=${pageNum * pageSize}${searchParam}${categoryParam}${noBarcodeParam}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/suppliers`, { headers: getAuthHeader() })
      ]);
      setProducts(productsRes.data || []);
      setSuppliers(suppliersRes.data || []);
      setPage(pageNum);
      
      // Get count separately to not block main load
      try {
        const countRes = await axios.get(`${API_URL}/products/count?${noBarcode ? 'no_barcode=true' : ''}${searchParam}${categoryParam}`, { headers: getAuthHeader() });
        setTotalProducts(countRes.data?.count ?? productsRes.data?.length ?? 0);
      } catch {
        // Unknown total — keep paging available while full pages keep coming back
        setTotalProducts((productsRes.data?.length || 0) === pageSize ? (pageNum + 2) * pageSize : pageNum * pageSize + (productsRes.data?.length || 0));
      }
    } catch (error) {
      console.error('Failed to load products:', error);
      toast.error('Failed to load products');
      setProducts([]);
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(0);
  }, []);

  // Reload the list you are looking at (same search, filter and page) so an edited product stays on screen
  const refreshList = () => {
    const cat = categoryFilter === 'wellness' ? 'Wellness Dispensary' : categoryFilter === 'nobarcode' ? 'nobarcode' : '';
    fetchData(page, searchTerm.length >= 2 ? searchTerm : '', cat);
  };

  // Debounced server-side search
  const searchTimeoutRef = useRef(null);
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    // Longer debounce (800ms) so the user can finish typing a word before the
    // server fetch fires. The list still filters CLIENT-SIDE instantly via
    // filteredProducts so search feels snappy without a network round-trip
    // on every keystroke.
    searchTimeoutRef.current = setTimeout(() => {
      const cat = categoryFilter === 'wellness' ? 'Wellness Dispensary' : categoryFilter === 'nobarcode' ? 'nobarcode' : '';
      if (searchTerm.length >= 2) {
        fetchData(0, searchTerm, cat);
      } else if (searchTerm.length === 0) {
        fetchData(0, '', cat);
      }
    }, 800);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [searchTerm]);

  const resetForm = () => {
    setFormData({ 
      name: '', barcode: '', supplier_code: '', brand: '', supplier_id: '', supplier_name: '',
      quantity: 0, buy_price: '', sell_price: '', tax_rate: 15, category: 'General',
      description: '',
      is_bulk: false, bulk_unit: 'gm', bulk_quantity_orewa: 0, bulk_quantity_eastridge: 0, portion_size: 0,
      image_url: ''
    });
    setImageFile(null);
    setImagePreview('');
    setGenerateBarcode(false);
  };

  // Handle restore stock from CSV
  const handleRestoreStock = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setRestoreLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/products/restore-stock`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(response.data.message);
      if (response.data.not_found_count > 0) {
        toast.warning(`${response.data.not_found_count} products not found by SKU`);
      }
      
      refreshList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore stock');
    } finally {
      setRestoreLoading(false);
      e.target.value = '';
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name.trim()) {
      toast.error('Product name is required');
      return;
    }
    
    const buyPrice = parseFloat(formData.buy_price);
    const sellPrice = parseFloat(formData.sell_price);
    
    if (!buyPrice || buyPrice <= 0) {
      toast.error('Buy price (wholesale) is required and must be greater than 0');
      return;
    }
    
    if (!sellPrice || sellPrice <= 0) {
      toast.error('Sell price (retail) is required and must be greater than 0');
      return;
    }
    
    if (sellPrice < buyPrice) {
      toast.error('Sell price should be higher than buy price');
      return;
    }
    
    // Margin check for staff — sell_price is inc-GST, buy_price ex-GST
    if (!isAdmin && sellPrice > 0 && buyPrice > 0) {
      const sellExGst = sellPrice / 1.15;
      const margin = ((sellExGst - buyPrice) / sellExGst) * 100;
      if (margin < 40) {
        toast.error(`Margin ${margin.toFixed(0)}% is below minimum 40%. Only admin can override.`);
        return;
      }
    }
    
    setFormLoading(true);
    try {
      const productData = { 
        ...formData,
        buy_price: buyPrice,
        sell_price: sellPrice
      };
      
      // If auto-generate barcode is checked, the backend will generate it
      if (generateBarcode) {
        productData.barcode = ''; // Clear so backend knows to generate
      }
      
      const headers = { ...getAuthHeader(), 'X-Staff-Role': activeStaff?.role || 'admin' };
      const response = await axios.post(`${API_URL}/products`, productData, { headers });
      const newProductId = response.data.id;
      
      // If we need to generate barcode
      if (generateBarcode && newProductId) {
        await axios.post(`${API_URL}/products/${newProductId}/generate-barcode`, {}, { headers: getAuthHeader() });
      }

      // Upload image if one was selected — must happen before Shopify publish
      // so the new product carries its photo to Shopify in the same flow.
      if (imageFile && newProductId) {
        try {
          const imageFormData = new FormData();
          imageFormData.append('file', imageFile);
          await axios.post(`${API_URL}/products/${newProductId}/upload-image`, imageFormData, {
            headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
          });
        } catch (imgErr) {
          toast.error('Product saved, but image upload failed: ' + (imgErr.response?.data?.detail || 'Unknown error'));
        }
      }
      
      // If publish to Shopify is checked
      if (publishToShopify && newProductId) {
        setPublishingToShopify(true);
        try {
          const shopifyResponse = await axios.post(
            `${API_URL}/shopify/create-product?product_id=${newProductId}`, 
            {}, 
            { headers: getAuthHeader() }
          );
          toast.success(shopifyResponse.data.message || 'Product published to Shopify');
        } catch (shopifyError) {
          toast.error('Product added but failed to publish to Shopify: ' + (shopifyError.response?.data?.detail || 'Unknown error'));
        } finally {
          setPublishingToShopify(false);
        }
      }
      
      toast.success('Product added successfully');
      setIsAddDialogOpen(false);
      resetForm();
      setPublishToShopify(false);
      refreshList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add product');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    
    // Margin check for staff
    if (!isAdmin) {
      const sell = parseFloat(formData.sell_price) || 0;
      const buy = parseFloat(formData.buy_price) || 0;
      if (sell > 0 && buy > 0) {
        // sell is inc-GST, buy is ex-GST — strip GST off sell first
        const sellExGst = sell / 1.15;
        const margin = ((sellExGst - buy) / sellExGst) * 100;
        if (margin < 40) {
          toast.error(`Margin ${margin.toFixed(0)}% is below minimum 40%. Only admin can override.`);
          return;
        }
      }
    }
    
    setFormLoading(true);
    try {
      // Upload image first if there's a new file
      if (imageFile && selectedProduct) {
        const imageFormData = new FormData();
        imageFormData.append('file', imageFile);
        await axios.post(`${API_URL}/products/${selectedProduct.id}/upload-image`, imageFormData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
        });
      }
      
      const headers = { ...getAuthHeader(), 'X-Staff-Role': activeStaff?.role || 'admin' };
      const payload = {
        ...formData,
        stock_orewa: formData.stock_orewa ?? 0,
        stock_eastridge: formData.stock_eastridge ?? 0,
        quantity: (formData.stock_orewa || 0) + (formData.stock_eastridge || 0),
        store: selectedStore
      };
      const putRes = await axios.put(`${API_URL}/products/${selectedProduct.id}`, payload, { headers });
      const sync = putRes.data?.shopify_sync;

      // If "Publish to Shopify" was ticked on Edit, push to Shopify too
      if (publishToShopify && selectedProduct.id) {
        setPublishingToShopify(true);
        try {
          const shopifyResponse = await axios.post(
            `${API_URL}/shopify/create-product?product_id=${selectedProduct.id}`,
            {},
            { headers: getAuthHeader() }
          );
          toast.success(shopifyResponse.data.message || 'Product published to Shopify');
        } catch (shopifyError) {
          toast.error('Product updated but Shopify publish failed: ' + (shopifyError.response?.data?.detail || 'Unknown error'));
        } finally {
          setPublishingToShopify(false);
        }
      }

      if (sync === 'synced') {
        toast.success('Product updated — Shopify price/details updated too ✓');
      } else if (sync && (sync.startsWith('failed') || sync.startsWith('error'))) {
        toast.error(`Product saved BUT the Shopify update FAILED (${sync}). Fix it on Shopify or retry, or the online price will be wrong!`, { duration: 10000 });
      } else if (sync === 'skipped_preview') {
        toast.success('Product updated (Shopify push skipped in preview)');
      } else {
        toast.success('Product updated successfully');
      }
      setIsEditDialogOpen(false);
      resetForm();
      setPublishToShopify(false);
      // Update the row in place so it stays on screen while you keep working — the list reloads on your next search
      const updated = { ...selectedProduct, ...payload, ...(putRes.data || {}) };
      setProducts(prev => prev.map(p => p.id === selectedProduct.id ? updated : p));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update product');
    } finally {
      setFormLoading(false);
    }
  };

  // Handle image file selection — accepts up to 10MB and auto-compresses large photos
  // (phone cameras produce 3–8MB JPGs) down to max 1600px / 85% JPEG so DB stays lean
  // and Shopify accepts the upload without timeout.
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type)) {
      toast.error('Only JPG, PNG, and WEBP images are allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    // If already small, use as-is
    if (file.size < 400 * 1024) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      return;
    }

    // Compress: read → draw on canvas at max 1600px → re-encode JPEG 0.85
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1600;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          if (width >= height) { height = Math.round(height * (MAX / width)); width = MAX; }
          else { width = Math.round(width * (MAX / height)); height = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (!blob) {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
            return;
          }
          const compressed = new File([blob], (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
          setImageFile(compressed);
          setImagePreview(URL.createObjectURL(compressed));
          const beforeKb = Math.round(file.size / 1024);
          const afterKb = Math.round(compressed.size / 1024);
          if (beforeKb > afterKb + 50) {
            toast.success(`Image optimised: ${beforeKb}KB → ${afterKb}KB`);
          }
        }, 'image/jpeg', 0.85);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Handle image URL input
  const handleImageUrlChange = (url) => {
    setFormData({ ...formData, image_url: url });
    setImagePreview(url);
    setImageFile(null);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API_URL}/products/${productId}`, { headers: getAuthHeader() });
      toast.success('Product deleted successfully');
      refreshList();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  // Generate Barcode for product
  const handleGenerateBarcode = async (product) => {
    if (product.barcode) {
      toast.error('Product already has a barcode');
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/products/${product.id}/generate-barcode`, {}, { headers: getAuthHeader() });
      toast.success(`Barcode generated: ${response.data.barcode}`);
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, barcode: response.data.barcode } : p));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate barcode');
    }
  };

  // Generate barcodes for all products without one
  const handleGenerateAllBarcodes = async () => {
    const productsWithoutBarcode = products.filter(p => !p.barcode);
    if (productsWithoutBarcode.length === 0) {
      toast.info('All products already have barcodes');
      return;
    }
    
    try {
      toast.info('Checking which products really need a barcode…');
      const dry = (await axios.post(`${API_URL}/products/generate-barcodes-bulk`, {}, { headers: getAuthHeader() })).data;
      const dupList = (dry.duplicates || []).slice(0, 8).map(d => `  • ${d.name}  →  already exists as "${d.existing_product}" (${d.existing_barcode})`).join('\n');
      if (!dry.count) {
        window.alert(`Nothing to generate.\n\n${dry.skipped_duplicates} product(s) without a barcode are duplicates of products that already have one — use Merge Duplicates for those:\n${dupList}`);
        return;
      }
      const ok = window.confirm(
        `BARCODE PLAN (nothing changed yet)\n\n` +
        `• ${dry.from_sku} product(s): the numeric SKU is the manufacturer barcode → it will be used as-is\n` +
        `• ${dry.system_codes} product(s): no barcode anywhere → ONE in-store 99… code each\n` +
        `• ${dry.skipped_duplicates} product(s) SKIPPED: same name as a product that already has a barcode (merge them instead)` +
        (dupList ? `\n${dupList}${dry.skipped_duplicates > 8 ? '\n  …' : ''}` : '') +
        `\n\nProducts that already have a barcode are never touched.\n\nClick OK to apply.`
      );
      if (!ok) return;
      const response = await axios.post(`${API_URL}/products/generate-barcodes-bulk?commit=true`, {}, { headers: getAuthHeader() });
      toast.success(`${response.data.count} barcodes set (${response.data.from_sku} from SKU, ${response.data.system_codes} in-store codes)`);
      refreshList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate barcodes');
    }
  };

  // Bulk select/deselect all filtered products
  const handleSelectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(p => p.id));
    }
  };

  // Toggle single product selection
  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  // Bulk update supplier for selected products
  const handleBulkUpdateSupplier = async () => {
    if (!bulkSupplierName.trim()) {
      toast.error('Please enter a supplier name');
      return;
    }
    if (selectedProducts.length === 0) {
      toast.error('No products selected');
      return;
    }

    setBulkUpdating(true);
    try {
      // Update each selected product
      let updated = 0;
      for (const productId of selectedProducts) {
        await axios.put(`${API_URL}/products/${productId}`, {
          supplier_name: bulkSupplierName.trim()
        }, { headers: getAuthHeader() });
        updated++;
      }
      
      toast.success(`Updated supplier for ${updated} products`);
      setSelectedProducts([]);
      setShowBulkEditSupplier(false);
      setBulkSupplierName('');
      refreshList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update supplier');
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) {
      toast.error('No products selected');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedProducts.length} product(s)? This cannot be undone.`)) return;
    
    try {
      const response = await axios.post(`${API_URL}/products/bulk-delete`, {
        product_ids: selectedProducts
      }, { headers: getAuthHeader() });
      toast.success(`Deleted ${response.data.deleted} products`);
      setSelectedProducts([]);
      refreshList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete products');
    }
  };
  
  // Bulk Import Handler
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['.csv', '.xlsx', '.xls'];
      const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      if (!validTypes.includes(fileExt)) {
        toast.error('Please select a CSV or Excel file (.csv, .xlsx)');
        return;
      }
      setImportFile(file);
      setImportResult(null);
    }
  };
  
  const handleBulkImport = async () => {
    if (!importFile) {
      toast.error('Please select a file first');
      return;
    }
    
    setImportLoading(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await axios.post(`${API_URL}/products/import`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`Successfully imported ${response.data.imported} products out of ${response.data.total_rows || '?'} rows`);
        refreshList();
      } else if (response.data.total_rows === 0) {
        toast.error('No data rows found in the file. Check the file format.');
      } else if (response.data.imported === 0 && response.data.failed > 0) {
        toast.error(`All ${response.data.failed} rows failed to import. Check column headers.`);
      }
      
      if (response.data.failed > 0 && response.data.imported > 0) {
        toast.warning(`${response.data.failed} products failed to import`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
      setImportResult({ success: false, error: error.response?.data?.detail || 'Import failed' });
    } finally {
      setImportLoading(false);
    }
  };
  
  const downloadTemplate = () => {
    const csvContent = "name,barcode,supplier,cost_price,retail_price,description,brand,stock_orewa,stock_eastridge,category,sku\nWhey Protein 2kg,1234567890123,NZ Supplements,45.00,79.99,Premium whey protein powder,OptimumNutrition,50,30,Protein,WP2KG001\nCreatine 500g,1234567890124,NZ Supplements,15.00,29.99,Micronized creatine monohydrate,MuscleTech,25,20,Supplements,CR500001";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'product_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleRemoveDuplicates = async () => {
    if (!window.confirm('This will remove duplicate products (keeping the most recent version of each). Continue?')) return;
    setRemovingDuplicates(true);
    try {
      const response = await axios.post(`${API_URL}/products/remove-duplicates`, {}, { headers: getAuthHeader() });
      if (response.data.products_removed > 0) {
        toast.success(`Removed ${response.data.products_removed} duplicates from ${response.data.duplicates_found} groups`);
        refreshList();
      } else {
        toast.info('No duplicate products found');
      }
    } catch (error) {
      toast.error('Failed to remove duplicates');
    } finally {
      setRemovingDuplicates(false);
    }
  };
  
  const resetImport = () => {
    setImportFile(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Stock Count Functions
  const initializeStockCount = () => {
    const items = products.map(p => ({
      id: p.id,
      name: p.name,
      barcode: p.barcode || '',
      brand: (p.brand || '').trim(),
      supplier_id: p.supplier_id || '',
      supplier_name: (p.supplier_name || '').trim(),
      system_orewa: p.stock_orewa || 0,
      system_eastridge: p.stock_eastridge || 0,
      counted_orewa: null,
      counted_eastridge: null,
      touched: false
    }));
    setStockCountItems(items);
    setIsStockCountOpen(true);
  };
  
  const updateCountedStock = (productId, store, value) => {
    setStockCountItems(stockCountItems.map(item => 
      item.id === productId 
        ? { 
            ...item, 
            [`counted_${store}`]: value === '' ? null : parseInt(value) || 0,
            touched: true 
          } 
        : item
    ));
  };
  
  const getStockDifference = (item, store) => {
    const counted = item[`counted_${store}`];
    const system = item[`system_${store}`];
    if (counted === null) return null;
    return counted - system;
  };
  
  const hasDiscrepancies = () => {
    return stockCountItems.some(item => {
      const diffOrewa = getStockDifference(item, 'orewa');
      const diffEastridge = getStockDifference(item, 'eastridge');
      return (diffOrewa !== null && diffOrewa !== 0) || (diffEastridge !== null && diffEastridge !== 0);
    });
  };
  
  const getDiscrepancyCount = () => {
    return stockCountItems.filter(item => {
      const diffOrewa = getStockDifference(item, 'orewa');
      const diffEastridge = getStockDifference(item, 'eastridge');
      return (diffOrewa !== null && diffOrewa !== 0) || (diffEastridge !== null && diffEastridge !== 0);
    }).length;
  };
  
  const handleSaveStockCount = async () => {
    const itemsToUpdate = stockCountItems.filter(item => {
      const diffOrewa = getStockDifference(item, 'orewa');
      const diffEastridge = getStockDifference(item, 'eastridge');
      return (diffOrewa !== null && diffOrewa !== 0) || (diffEastridge !== null && diffEastridge !== 0);
    });
    
    if (itemsToUpdate.length === 0) {
      toast.info('No stock changes to save');
      return;
    }
    
    setStockCountLoading(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const item of itemsToUpdate) {
      try {
        const updateData = {};
        if (item.counted_orewa !== null) {
          updateData.stock_orewa = item.counted_orewa;
        }
        if (item.counted_eastridge !== null) {
          updateData.stock_eastridge = item.counted_eastridge;
        }
        
        await axios.put(`${API_URL}/products/${item.id}`, updateData, { headers: getAuthHeader() });
        successCount++;
      } catch (error) {
        failCount++;
      }
    }
    
    setStockCountLoading(false);
    
    if (successCount > 0) {
      toast.success(`Updated stock for ${successCount} products`);
      refreshList();
      setIsStockCountOpen(false);
    }
    if (failCount > 0) {
      toast.error(`Failed to update ${failCount} products`);
    }
  };
  
  const filteredStockCountItems = stockCountItems.filter(item => {
    // Whitespace + case insensitive matching for brand and supplier
    if (stockCountBrand !== 'all') {
      if ((item.brand || '').trim().toLowerCase() !== stockCountBrand.toLowerCase()) return false;
    }
    if (stockCountSupplier !== 'all') {
      if ((item.supplier_name || '').trim().toLowerCase() !== stockCountSupplier.toLowerCase()) return false;
    }
    if (!stockCountSearch) return true;
    const q = stockCountSearch.toLowerCase();
    return (
      item.name.toLowerCase().includes(q) ||
      item.barcode.includes(stockCountSearch) ||
      (item.brand || '').toLowerCase().includes(q) ||
      (item.supplier_name || '').toLowerCase().includes(q)
    );
  });

  // Distinct brands available in the current stock count list (for the dropdown).
  // Build from the FULL products list, not just trimmed items, so every brand appears.
  const stockCountBrands = Array.from(
    new Set(products.map(p => (p.brand || '').trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  // Distinct suppliers — pull from the suppliers list directly so every active
  // supplier shows even if no product is currently linked to them.
  const stockCountSuppliers = Array.from(
    new Set([
      ...suppliers.map(s => (s.name || '').trim()).filter(Boolean),
      ...products.map(p => (p.supplier_name || '').trim()).filter(Boolean),
    ])
  ).sort((a, b) => a.localeCompare(b));

  // Print a paper stock-take sheet (PDF) for the currently filtered products.
  // Active store only — blank "Counted" column for staff to fill in by hand.
  const handlePrintStockCountSheet = () => {
    try {
      const items = filteredStockCountItems;
      if (items.length === 0) {
        toast.error('No products to print. Adjust your search/brand filter.');
        return;
      }
      // Active store label + which system count column to print
      const storeLabel = storeName(selectedStore);
      const systemKey = selectedStore === 'orewa' ? 'system_orewa' : 'system_eastridge';

      const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const today = new Date().toLocaleDateString('en-NZ');

      // Header
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.text('STOCK TAKE SHEET', 105, 15, { align: 'center' });
      doc.setFontSize(11);
      doc.text(`${localStorage.getItem('business_name') || 'SupplyTrack'} — ${storeLabel}`, 105, 22, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const filterParts = [];
      if (stockCountBrand !== 'all') filterParts.push(`Brand: ${stockCountBrand}`);
      if (stockCountSupplier !== 'all') filterParts.push(`Supplier: ${stockCountSupplier}`);
      if (stockCountSearch) filterParts.push(`Search: "${stockCountSearch}"`);
      const filterText = filterParts.length ? filterParts.join('  •  ') : 'All products';
      doc.text(`${filterText}  •  ${items.length} products  •  Printed: ${today}`, 105, 28, { align: 'center' });

      // Table — sorted alphabetically by name (easier to walk shelf in order)
      const rows = items
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(p => [
          p.name,
          p.barcode || '-',
          String(p[systemKey] ?? 0),
          '' // blank "Counted" cell
        ]);

      autoTable(doc, {
        startY: 33,
        head: [['Product', 'Barcode', 'System', 'Counted']],
        body: rows,
        styles: { font: 'helvetica', fontSize: 9, cellPadding: 1.8, lineColor: [180, 180, 180], lineWidth: 0.1, minCellHeight: 7 },
        headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: {
          0: { cellWidth: 95 },        // Product name
          1: { cellWidth: 35 },        // Barcode
          2: { cellWidth: 18, halign: 'center' }, // System
          3: { cellWidth: 32, halign: 'center' }, // Counted (blank)
        },
        margin: { left: 8, right: 8 },
        didDrawPage: (data) => {
          // Footer with signature line on each page
          const pageH = doc.internal.pageSize.getHeight();
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          doc.text(`Counted by: _____________________   Date: _____ / _____ / _____`, 8, pageH - 8);
          doc.text(`Page ${data.pageNumber}`, 200, pageH - 8, { align: 'right' });
        },
      });

      const fileName = `stock-take-${storeLabel.toLowerCase()}-${stockCountBrand === 'all' ? 'all' : stockCountBrand.toLowerCase().replace(/\s+/g, '-')}-${today.replace(/\//g, '-')}.pdf`;
      doc.save(fileName);
      toast.success(`Stock take sheet downloaded (${items.length} products)`);
    } catch (err) {
      console.error('Print stock sheet failed:', err);
      toast.error('Failed to generate stock take sheet');
    }
  };

  const openEditDialog = (product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name,
      barcode: product.barcode || '',
      alt_barcodes: product.alt_barcodes || [],
      supplier_code: product.supplier_code || '',
      brand: product.brand || '',
      supplier_id: product.supplier_id || '',
      supplier_name: product.supplier_name || '',
      tax_rate: product.tax_rate ?? 15,
      quantity: product.quantity,
      stock_orewa: product.stock_orewa || 0,
      stock_eastridge: product.stock_eastridge || 0,
      buy_price: product.buy_price,
      sell_price: product.sell_price,
      category: product.category,
      description: product.description || '',
      is_bulk: product.is_bulk || false,
      bulk_unit: product.bulk_unit || 'gm',
      bulk_quantity_orewa: product.bulk_quantity_orewa || 0,
      bulk_quantity_eastridge: product.bulk_quantity_eastridge || 0,
      portion_size: product.portion_size || 0,
      image_url: product.image_url || ''
    });
    setImagePreview(product.image_url || '');
    setImageFile(null);
    setPublishToShopify(false);
    setIsEditDialogOpen(true);
  };

  const getStockStatus = (quantity) => {
    if (quantity === 0) return { label: 'Out of Stock', class: 'status-out-of-stock' };
    if (quantity < 10) return { label: 'Low Stock', class: 'status-low-stock' };
    return { label: 'In Stock', class: 'status-in-stock' };
  };

  // Nutriscript scraper functions
  const startNutriscriptImport = async () => {
    try {
      setNutriscriptLoading(true);
      setNutriscriptStatus({ running: true, message: 'Starting...', imported: 0, skipped: 0, total: 0, progress: 0 });
      await axios.post(`${API_URL}/nutriscript/scrape`, {}, { headers: getAuthHeader() });
      
      // Start polling for status
      nutriscriptPollRef.current = setInterval(async () => {
        try {
          const res = await axios.get(`${API_URL}/nutriscript/scrape/status`, { headers: getAuthHeader() });
          setNutriscriptStatus(res.data);
          if (res.data.finished || !res.data.running) {
            clearInterval(nutriscriptPollRef.current);
            nutriscriptPollRef.current = null;
            setNutriscriptLoading(false);
            if (res.data.imported > 0) {
              toast.success(`Imported ${res.data.imported} products from Nutriscript`);
              fetchData(page, '', categoryFilter === 'wellness' ? 'Wellness Dispensary' : categoryFilter === 'nobarcode' ? 'nobarcode' : '');
            }
          }
        } catch { /* ignore poll errors */ }
      }, 2000);
    } catch (err) {
      toast.error('Failed to start Nutriscript import');
      setNutriscriptLoading(false);
    }
  };

  // Clean up poll on unmount
  useEffect(() => {
    return () => {
      if (nutriscriptPollRef.current) clearInterval(nutriscriptPollRef.current);
    };
  }, []);

  // Shopify publish functions
  const handleCategoryFilter = (filter) => {
    setCategoryFilter(filter);
    setSelectedProducts([]);
    const cat = filter === 'wellness' ? 'Wellness Dispensary' : filter === 'nobarcode' ? 'nobarcode' : '';
    const search = searchTerm.length >= 2 ? searchTerm : '';
    fetchData(0, search, cat);
  };

  const openPublishDialog = async () => {
    setPublishResult(null);
    setSelectedCollection('');
    // Pre-fill prices from selected products
    const prices = {};
    filteredProducts.filter(p => selectedProducts.includes(p.id)).forEach(p => {
      prices[p.id] = p.sell_price || 0;
    });
    setPublishPrices(prices);
    setIsPublishDialogOpen(true);

    // Fetch collections
    setCollectionsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/shopify/collections`, { headers: getAuthHeader() });
      setShopifyCollections(res.data?.collections || []);
    } catch {
      toast.error('Could not load Shopify collections');
    } finally {
      setCollectionsLoading(false);
    }
  };

  const handleBulkPublish = async () => {
    const items = selectedProducts.map(id => ({
      product_id: id,
      sell_price: publishPrices[id] !== undefined ? Number(publishPrices[id]) : null,
    }));
    setPublishing(true);
    try {
      const res = await axios.post(`${API_URL}/shopify/bulk-publish`, {
        products: items,
        collection_id: (selectedCollection && selectedCollection !== 'none') ? selectedCollection : null,
      }, { headers: getAuthHeader() });
      setPublishResult(res.data);
      if (res.data.published > 0) {
        toast.success(`Published ${res.data.published} products to Shopify`);
        fetchData(page, '', categoryFilter === 'wellness' ? 'Wellness Dispensary' : categoryFilter === 'nobarcode' ? 'nobarcode' : '');
      }
    } catch (err) {
      toast.error('Publish failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setPublishing(false);
    }
  };

  // Transfer to Royal Oak functions
  const cleanText = (str) => (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const openTransferDialog = () => {
    setTransferResult(null);
    const qtys = {};
    filteredProducts.filter(p => selectedProducts.includes(p.id)).forEach(p => {
      const storeStock = selectedStore === 'eastridge' ? (p.stock_eastridge || 0) : (p.stock_orewa || 0);
      qtys[p.id] = Math.min(1, storeStock);
    });
    setTransferQuantities(qtys);
    setIsTransferDialogOpen(true);
  };

  const generateTransferInvoice = (result) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const transferId = result.transfer_id;

    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('STOCK TRANSFER INVOICE', 14, 22);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(`Transfer #${transferId}`, pageWidth - 14, 22, { align: 'right' });
    doc.text(`Date: ${new Date().toLocaleDateString('en-NZ')}`, pageWidth - 14, 28, { align: 'right' });

    doc.setDrawColor(200);
    doc.line(14, 32, pageWidth - 14, 32);

    // From / To
    doc.setTextColor(0);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('FROM:', 14, 40);
    doc.text('TO:', 110, 40);

    doc.setFont('helvetica', 'normal');
    doc.setFont('helvetica', 'bold');
    doc.text(localStorage.getItem('business_name') || 'SupplyTrack', 14, 46);
    doc.setFont('helvetica', 'normal');
    doc.text(cleanText(result.source_store.charAt(0).toUpperCase() + result.source_store.slice(1)), 14, 51);

    doc.setFont('helvetica', 'bold');
    doc.text(cleanText(result.destination), 110, 46);
    doc.setFont('helvetica', 'normal');

    // Items table — uses WHOLESALE (buy) price for inter-store transfer billing
    const tableData = result.items.map((item, i) => [
      i + 1,
      cleanText(item.name),
      item.sku || '',
      item.quantity,
      `$${(item.buy_price || 0).toFixed(2)}`,
      `$${((item.buy_price || 0) * item.quantity).toFixed(2)}`,
    ]);

    autoTable(doc, {
      startY: 58,
      head: [['#', 'Product', 'SKU', 'Qty', 'Wholesale', 'Total']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [50, 50, 50], fontSize: 9, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8.5 },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 65 },
        2: { cellWidth: 30 },
        3: { cellWidth: 15, halign: 'center' },
        4: { cellWidth: 25, halign: 'right' },
        5: { cellWidth: 25, halign: 'right' },
      },
      margin: { left: 14, right: 14 },
    });

    // Totals (wholesale value of the transfer)
    const finalY = doc.lastAutoTable.finalY + 8;
    const totalQty = result.items.reduce((s, i) => s + i.quantity, 0);
    const totalValue = result.items.reduce((s, i) => s + (i.buy_price || 0) * i.quantity, 0);

    // Wholesale prices are ex GST — Royal Oak is a separate company, so GST (15%) is charged on the transfer
    const gstValue = totalValue * 0.15;
    const totalIncGst = totalValue + gstValue;
    doc.setFontSize(10);
    doc.text(`Total Items: ${totalQty}`, 14, finalY);
    doc.setFont('helvetica', 'normal');
    doc.text('Subtotal (ex GST):', pageWidth - 60, finalY);
    doc.text(`$${totalValue.toFixed(2)}`, pageWidth - 14, finalY, { align: 'right' });
    doc.text('GST 15%:', pageWidth - 60, finalY + 6);
    doc.text(`$${gstValue.toFixed(2)}`, pageWidth - 14, finalY + 6, { align: 'right' });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Total (inc GST):', pageWidth - 60, finalY + 13);
    doc.text(`$${totalIncGst.toFixed(2)}`, pageWidth - 14, finalY + 13, { align: 'right' });

    // Signature lines
    const sigY = finalY + 38;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(80);
    doc.line(14, sigY, 90, sigY);
    doc.text('Transferred by (sign)', 14, sigY + 5);
    doc.line(110, sigY, 196, sigY);
    doc.text('Received by (sign)', 110, sigY + 5);

    doc.line(14, sigY + 18, 90, sigY + 18);
    doc.text('Date', 14, sigY + 23);
    doc.line(110, sigY + 18, 196, sigY + 18);
    doc.text('Date', 110, sigY + 23);

    const fileName = `Transfer_${transferId}_Royal_Oak.pdf`;
    doc.save(fileName);
    return fileName;
  };

  const handleTransfer = async () => {
    const items = selectedProducts
      .filter(id => (transferQuantities[id] || 0) > 0)
      .map(id => ({ product_id: id, quantity: Number(transferQuantities[id]) }));

    if (items.length === 0) {
      toast.error('Set quantities for at least one product');
      return;
    }

    setTransferring(true);
    try {
      const res = await axios.post(`${API_URL}/products/transfer`, {
        items,
        source_store: selectedStore || 'orewa',
        destination: 'Health2000 Royal Oak',
        notes: '',
      }, { headers: getAuthHeader() });

      setTransferResult(res.data);

      if (res.data.transferred > 0) {
        const fileName = generateTransferInvoice(res.data);
        toast.success(`Transferred ${res.data.transferred} products. Invoice downloaded as ${fileName}`);
        fetchData(page, '', categoryFilter === 'wellness' ? 'Wellness Dispensary' : categoryFilter === 'nobarcode' ? 'nobarcode' : '');
      }
      if (res.data.errors?.length > 0) {
        res.data.errors.forEach(e => toast.error(`${e.name || e.product_id}: ${e.error}`));
      }
    } catch (err) {
      toast.error('Transfer failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setTransferring(false);
    }
  };

  const filteredProducts = (() => {
    const raw = (searchTerm || '').trim();
    if (!raw) return products;
    // Barcode shortcut (exact substring on full term)
    const barcodeHits = products.filter(p => p.barcode && p.barcode.includes(raw));
    // Multi-word AND search across name/category/brand/supplier
    const words = raw.toLowerCase().split(/\s+/).filter(Boolean);
    const wordHits = products.filter(p => {
      const haystack = [
        p.name || '',
        p.category || '',
        p.brand || '',
        p.supplier_name || '',
      ].join(' ').toLowerCase();
      return words.every(w => haystack.includes(w));
    });
    // Merge unique (barcode hits first)
    const seen = new Set();
    return [...barcodeHits, ...wordHits].filter(p => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  })();

  // Find similar products based on brand/category
  const getSimilarProducts = (product) => {
    if (!product) return [];
    return products.filter(p => 
      p.id !== product.id && 
      (p.brand === product.brand || p.category === product.category)
    ).slice(0, 5);
  };

  // ==================== Price List Update ====================
  const handlePriceListUpload = async () => {
    if (!priceListFile) return;
    setPriceListLoading(true);
    setPriceListResult(null);
    try {
      const formData = new FormData();
      formData.append('file', priceListFile);
      const response = await axios.post(`${API_URL}/products/parse-pricelist`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      setPriceListResult(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse price list');
    }
    setPriceListLoading(false);
  };

  const togglePriceItem = (index) => {
    setPriceListResult(prev => {
      const updated = { ...prev };
      updated.matches = [...prev.matches];
      updated.matches[index] = { ...updated.matches[index], selected: !updated.matches[index].selected };
      return updated;
    });
  };

  const applyPriceUpdates = async () => {
    if (!priceListResult) return;
    const selected = priceListResult.matches.filter(m => m.selected);
    if (selected.length === 0) {
      toast.error('No items selected');
      return;
    }
    setPriceListApplying(true);
    try {
      const response = await axios.post(`${API_URL}/products/apply-pricelist`, selected, {
        headers: getAuthHeader()
      });
      toast.success(`Updated prices for ${response.data.updated} products`);
      setIsPriceListOpen(false);
      setPriceListFile(null);
      setPriceListResult(null);
      fetchData(page);
    } catch (err) {
      toast.error('Failed to apply price updates');
    }
    setPriceListApplying(false);
  };

  // Initial mount only — show full spinner. After the first load, we never
  // unmount the page on refetch because that would steal focus from the search
  // input mid-typing and reset the user's scroll position after every edit.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  useEffect(() => { if (!loading) setHasLoadedOnce(true); }, [loading]);

  if (loading && !hasLoadedOnce) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <LabelPrintDialog
        products={labelDialogProducts || []}
        store={selectedStore}
        open={!!labelDialogProducts}
        onClose={() => setLabelDialogProducts(null)}
      />
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            Inventory
          </h1>
          <p className="text-slate-500 mt-1">Manage your supplement products</p>
        </div>
        <div className="flex gap-2">
          {/* Generate All Barcodes Button */}
          <Button 
            variant="outline"
            className="border-purple-500 text-purple-600 hover:bg-purple-50"
            onClick={handleGenerateAllBarcodes}
            data-testid="generate-all-barcodes-button"
          >
            <Barcode className="w-4 h-4 mr-2" /> Generate Barcodes
          </Button>

          {/* Export catalogue + bulk-edit re-upload */}
          <Button
            variant="outline"
            className="border-sky-500 text-sky-600 hover:bg-sky-50"
            data-testid="export-csv-button"
            disabled={exportingCsv}
            onClick={async () => {
              setExportingCsv(true);
              try {
                const res = await axios.get(`${API_URL}/products/export-csv`, { headers: getAuthHeader(), responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a');
                a.href = url;
                a.download = `inventory_export_${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                toast.success('Catalogue exported — edit in Excel, keep the id column, then use Upload Edits');
              } catch (err) {
                toast.error('Export failed');
              } finally {
                setExportingCsv(false);
              }
            }}
          >
            <Download className="w-4 h-4 mr-2" /> {exportingCsv ? 'Exporting…' : 'Export CSV'}
          </Button>
          <input
            type="file"
            accept=".csv,.xlsx"
            ref={bulkEditInputRef}
            className="hidden"
            data-testid="upload-edits-input"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (!f) return;
              setUploadingEdits(true);
              try {
                const fd = new FormData();
                fd.append('file', f);
                const res = await axios.post(`${API_URL}/products/bulk-update-csv`, fd, { headers: { ...getAuthHeader() } });
                const d = res.data;
                toast.success(d.message, { duration: 10000 });
                if (d.errors?.length) toast.error(`Some rows had issues: ${d.errors.slice(0, 3).join('; ')}`, { duration: 12000 });
                refreshList();
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Upload failed');
              } finally {
                setUploadingEdits(false);
              }
            }}
          />
          <Button
            variant="outline"
            className="border-sky-500 text-sky-600 hover:bg-sky-50"
            data-testid="upload-edits-button"
            disabled={uploadingEdits}
            onClick={() => bulkEditInputRef.current?.click()}
          >
            <Upload className="w-4 h-4 mr-2" /> {uploadingEdits ? 'Updating…' : 'Upload Edits'}
          </Button>
          
          {/* Bulk Import Button */}
          <Dialog open={isImportDialogOpen} onOpenChange={(open) => { setIsImportDialogOpen(open); if (!open) resetImport(); }}>
            <DialogTrigger asChild>
              <Button 
                variant="outline"
                className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                data-testid="bulk-import-button"
              >
                <Upload className="w-4 h-4 mr-2" /> Bulk Import
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-emerald-500" /> Bulk Import Products
                </DialogTitle>
                <DialogDescription>
                  Upload a CSV or Excel file to import multiple products at once
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Download Template */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-sm">
                  <div>
                    <p className="text-sm font-medium">Need a template?</p>
                    <p className="text-xs text-slate-500">Download our sample CSV template</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={downloadTemplate}>
                    <Download className="w-4 h-4 mr-1" /> Template
                  </Button>
                </div>
                
                {/* File Upload */}
                <div className="space-y-2">
                  <Label>Select File (CSV or Excel)</Label>
                  <div 
                    className={`border-2 border-dashed rounded-sm p-6 text-center cursor-pointer transition-colors ${
                      importFile ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 hover:border-slate-400'
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="import-file-input"
                    />
                    {importFile ? (
                      <div className="flex items-center justify-center gap-2 text-emerald-600">
                        <FileSpreadsheet className="w-8 h-8" />
                        <div className="text-left">
                          <p className="font-medium">{importFile.name}</p>
                          <p className="text-xs text-slate-500">{(importFile.size / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-10 h-10 mx-auto mb-2 text-slate-400" />
                        <p className="text-sm text-slate-600">Click to select or drag & drop</p>
                        <p className="text-xs text-slate-400 mt-1">Supports: .csv, .xlsx</p>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Import Result */}
                {importResult && (
                  <div className={`p-4 rounded-sm ${importResult.success ? (importResult.imported > 0 ? 'bg-emerald-50' : 'bg-amber-50') : 'bg-red-50'}`}>
                    {importResult.success ? (
                      <>
                        <div className={`flex items-center gap-2 mb-2 ${importResult.imported > 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                          {importResult.imported > 0 ? <CheckCircle className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                          <span className="font-medium">Import Complete</span>
                        </div>
                        <div className="text-sm space-y-1">
                          <p className="text-emerald-600">✓ {importResult.imported} products imported</p>
                          {importResult.failed > 0 && (
                            <p className="text-amber-600">⚠ {importResult.failed} products failed</p>
                          )}
                          {importResult.total_rows !== undefined && (
                            <p className="text-slate-500">Total rows in file: {importResult.total_rows}</p>
                          )}
                        </div>
                        {importResult.detected_headers?.length > 0 && (
                          <div className="mt-2 text-xs text-slate-600 bg-slate-100 p-2 rounded">
                            <p className="font-medium">Detected columns: {importResult.detected_headers.join(', ')}</p>
                          </div>
                        )}
                        {importResult.errors?.length > 0 && (
                          <div className="mt-2 text-xs text-red-600">
                            <p className="font-medium">Errors:</p>
                            {importResult.errors.slice(0, 5).map((err, i) => (
                              <p key={i}>• {err.row}: {err.error}</p>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="flex items-center gap-2 text-red-700">
                        <XCircle className="w-5 h-5" />
                        <span>{importResult.error || 'Import failed'}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Expected Columns */}
                <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-sm">
                  <p className="font-medium mb-1">Expected columns:</p>
                  <p><strong>Required:</strong> name</p>
                  <p><strong>Optional:</strong> barcode, supplier, cost_price, retail_price, description, brand, stock_orewa, stock_eastridge, category, sku</p>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => { setIsImportDialogOpen(false); resetImport(); }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleBulkImport}
                    disabled={!importFile || importLoading}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    data-testid="import-submit"
                  >
                    {importLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                    Import Products
                  </Button>
                </div>
                
                {/* Remove Duplicates */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-800">Have duplicate products?</p>
                      <p className="text-xs text-blue-600">Remove duplicates keeping the most recent version</p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleRemoveDuplicates}
                      disabled={removingDuplicates}
                      data-testid="remove-duplicates-btn"
                      className="border-blue-300 text-blue-700 hover:bg-blue-100"
                    >
                      {removingDuplicates ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                      Remove Duplicates
                    </Button>
                  </div>
                </div>
                
                {/* Restore Stock from CSV */}
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Restore Stock Levels
                  </h4>
                  <p className="text-sm text-amber-700 mb-3">
                    Upload a CSV with SKU and Quantity columns to fix stock levels.
                  </p>
                  <input 
                    type="file" 
                    accept=".csv" 
                    onChange={handleRestoreStock}
                    className="hidden"
                    id="restore-stock-file"
                    disabled={restoreLoading}
                  />
                  <label htmlFor="restore-stock-file">
                    <Button 
                      asChild
                      variant="outline" 
                      className="border-amber-400 text-amber-700 hover:bg-amber-100 cursor-pointer"
                      disabled={restoreLoading}
                    >
                      <span>
                        {restoreLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                        Restore Stock from CSV
                      </span>
                    </Button>
                  </label>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Merge Duplicates Button */}
          <Button
            variant="outline"
            className="border-purple-400 text-purple-700 hover:bg-purple-50 font-semibold"
            data-testid="merge-duplicates-button"
            onClick={async () => {
              const supplierFilter = window.prompt(
                'MERGE DUPLICATES — dry-run preview\n\n' +
                'I will show you the full list of pairs (downloadable as CSV) BEFORE anything is changed.\n\n' +
                'Type a supplier name to limit (e.g. "bepure", "good health"), OR leave blank to scan ALL suppliers.\n\n' +
                'Cancel to abort.'
              );
              if (supplierFilter === null) return;
              try {
                const url = supplierFilter.trim()
                  ? `${API_URL}/products/smart-merge-duplicates?supplier_name=${encodeURIComponent(supplierFilter.trim())}`
                  : `${API_URL}/products/smart-merge-duplicates`;
                toast.info('Analysing duplicates… this may take 10-20 seconds.');
                const dry = await axios.post(url, {}, { headers: getAuthHeader() });
                const d = dry.data || {};
                if (!d.groups_found) {
                  toast.success('No duplicates found' + (supplierFilter ? ` for "${supplierFilter}"` : ''));
                  return;
                }
                // Build CSV of the full plan
                const headers = [
                  'Group Name', 'Keeper ID', 'Keeper Barcode', 'Keeper Stock Orewa', 'Keeper Stock Eastridge',
                  'Loser ID', 'Loser Barcode', 'Loser Stock Orewa', 'Loser Stock Eastridge',
                  'Merged Stock Orewa', 'Merged Stock Eastridge', 'Merged Total', 'Stock Recovered',
                ];
                const rows = [];
                (d.plan || []).forEach(p => {
                  (p.losers || []).forEach(L => {
                    rows.push([
                      `"${(p.name || '').replace(/"/g, '""')}"`,
                      p.keeper?.id || '',
                      p.keeper?.barcode || '',
                      p.keeper?.stock_orewa ?? 0,
                      p.keeper?.stock_eastridge ?? 0,
                      L.id || '',
                      L.barcode || '',
                      L.stock_orewa ?? 0,
                      L.stock_eastridge ?? 0,
                      p.merged_stock?.orewa ?? 0,
                      p.merged_stock?.eastridge ?? 0,
                      p.merged_stock?.total ?? 0,
                      p.stock_recovered_from_losers ?? 0,
                    ].join(','));
                  });
                });
                const csv = [headers.join(','), ...rows].join('\n');
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const downloadUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
                link.download = `merge_duplicates_plan_${supplierFilter || 'all'}_${timestamp}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(downloadUrl);
                toast.success('Plan downloaded as CSV — review it, then come back to commit if you want.');

                // Now ask if they want to commit
                const samples = (d.plan || []).slice(0, 5)
                  .map(p => `  • ${p.name}  (${p.losers.length + 1} → 1, total stock ${p.merged_stock.total})`)
                  .join('\n');
                const ok = window.confirm(
                  `DRY-RUN COMPLETE — full plan downloaded as CSV.\n\n` +
                  `Duplicate groups       : ${d.groups_found}\n` +
                  `Products to delete      : ${d.products_to_delete}\n` +
                  `Stock units recovered    : ${d.stock_recovered}\n\n` +
                  `Sample (first 5 groups):\n${samples}\n\n` +
                  `REVIEW THE CSV FIRST.\n` +
                  `Click OK to commit, Cancel to abort and decide later.`
                );
                if (!ok) { toast.info('Merge NOT committed. CSV saved for your review.'); return; }
                const commitUrl = url + (url.includes('?') ? '&' : '?') + 'commit=true';
                const res = await axios.post(commitUrl, {}, { headers: getAuthHeader() });
                const r = res.data || {};
                window.alert(
                  `MERGE COMPLETE\n\n` +
                  `Groups merged: ${r.groups_found}\n` +
                  `Products deleted: ${r.products_to_delete}\n` +
                  `Stock recovered: ${r.stock_recovered} units\n\n` +
                  `Hard refresh to see the cleaned-up list.`
                );
                refreshList();
              } catch (err) {
                toast.error('Merge failed: ' + (err?.response?.data?.detail || err.message));
              }
            }}
          >
            Merge Duplicates
          </Button>

          {/* Fix products that got a system 99… barcode on top of their real one */}
          <Button
            variant="outline"
            className="border-amber-500 text-amber-700 hover:bg-amber-50 font-semibold"
            data-testid="fix-system-barcodes-button"
            onClick={async () => {
              try {
                toast.info('Checking products with system 99… barcodes…');
                const H = { headers: getAuthHeader() };
                const [mergeDry, skuDry, sc] = await Promise.all([
                  axios.post(`${API_URL}/products/smart-merge-duplicates?system_coded_only=true`, {}, H),
                  axios.post(`${API_URL}/products/barcodes/link-sku-codes`, {}, H),
                  axios.get(`${API_URL}/products/barcodes/system-coded`, H),
                ]);
                const m = mergeDry.data || {}; const k = skuDry.data || {};
                const mergeList = (m.plan || []).slice(0, 8).map(p => `  • ${p.name}: keeps ${p.all_barcodes_after.join(' + ')}`).join('\n');
                const skuList = (k.products || []).slice(0, 8).map(p => `  • ${p.name}: real barcode ${p.new_primary} (99 label still scans)`).join('\n');
                if (!m.groups_found && !k.count) {
                  window.alert(
                    `Nothing to repair automatically.\n\n${sc.data.count} product(s) carry an in-store 99… barcode` +
                    (sc.data.with_alt ? ` — ${sc.data.with_alt} of them already have their real barcode linked too` : '') + '.\n\n' +
                    'For any whose real barcode does not scan: scan it at the POS → "Barcode not found" → pick the product. Both barcodes work from then on.' +
                    (sc.data.with_shopify ? `\n\n${sc.data.with_shopify} are linked to Shopify — "Recover real barcodes from Shopify" (Ecommerce page) pulls their real barcodes automatically.` : '')
                  );
                  return;
                }
                const ok = window.confirm(
                  `REPAIR PLAN (nothing changed yet)\n\n` +
                  (m.groups_found ? `• ${m.groups_found} product(s) exist twice — one with the real barcode, one with a 99… code. Each pair becomes ONE product keeping BOTH barcodes; stock is added together and sales history moved across.\n${mergeList}${m.groups_found > 8 ? '\n  …' : ''}\n\n` : '') +
                  (k.count ? `• ${k.count} product(s) have their real barcode stored as the SKU. The real barcode becomes the main one and the printed 99… label keeps scanning.\n${skuList}${k.count > 8 ? '\n  …' : ''}\n\n` : '') +
                  `No barcode is ever removed.\n\nClick OK to apply.`
                );
                if (!ok) return;
                const [mr, kr] = await Promise.all([
                  m.groups_found ? axios.post(`${API_URL}/products/smart-merge-duplicates?system_coded_only=true&commit=true`, {}, H) : Promise.resolve({ data: { groups_found: 0 } }),
                  k.count ? axios.post(`${API_URL}/products/barcodes/link-sku-codes?commit=true`, {}, H) : Promise.resolve({ data: { count: 0 } }),
                ]);
                toast.success(`Repaired ${mr.data.groups_found} duplicate pair(s) and linked ${kr.data.count} real barcode(s) — everything scans`);
                refreshList();
              } catch (err) {
                toast.error('Fix failed: ' + (err?.response?.data?.detail || err.message));
              }
            }}
          >
            <Barcode className="w-4 h-4 mr-2" /> Fix 99… Barcodes
          </Button>

          {/* Update Prices Button (legacy) */}
          <Dialog open={isPriceListOpen} onOpenChange={(open) => { setIsPriceListOpen(open); if (!open) { setPriceListFile(null); setPriceListResult(null); } }}>
            <DialogTrigger asChild>
              <Button 
                variant="outline"
                className="border-amber-500 text-amber-600 hover:bg-amber-50"
                data-testid="update-prices-button"
              >
                <DollarSign className="w-4 h-4 mr-2" /> Update Prices
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-amber-500" /> Update Prices from Supplier List
                </DialogTitle>
                <DialogDescription>
                  Upload a supplier price list (CSV, Excel, or PDF) to automatically update your buy prices and recalculate sell prices based on existing markup.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-2">
                {!priceListResult ? (
                  <>
                    <div 
                      className="border-2 border-dashed border-amber-300 rounded-lg p-6 text-center cursor-pointer hover:border-amber-500 hover:bg-amber-50/50 transition-colors"
                      onClick={() => priceListInputRef.current?.click()}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setPriceListFile(f); }}
                      data-testid="pricelist-dropzone"
                    >
                      <Upload className="w-8 h-8 mx-auto text-amber-400 mb-2" />
                      <p className="text-sm font-medium text-slate-700">
                        {priceListFile ? priceListFile.name : 'Drop your price list here or click to browse'}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">Supports CSV, Excel (.xlsx), and PDF files</p>
                      <input
                        ref={priceListInputRef}
                        type="file"
                        accept=".csv,.xlsx,.xls,.pdf"
                        className="hidden"
                        onChange={(e) => { if (e.target.files[0]) setPriceListFile(e.target.files[0]); }}
                        data-testid="pricelist-file-input"
                      />
                    </div>
                    {priceListFile && (
                      <Button 
                        onClick={handlePriceListUpload} 
                        disabled={priceListLoading}
                        className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                        data-testid="pricelist-upload-button"
                      >
                        {priceListLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analysing price list...</> : <><Upload className="w-4 h-4 mr-2" /> Upload & Match Products</>}
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    {/* Summary */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-slate-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-slate-800">{priceListResult.total_file_items}</p>
                        <p className="text-xs text-slate-500">Items in File</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-green-700">{priceListResult.matched}</p>
                        <p className="text-xs text-green-600">Matched</p>
                      </div>
                      <div className="bg-red-50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-red-600">{priceListResult.unmatched_count}</p>
                        <p className="text-xs text-red-500">Unmatched</p>
                      </div>
                    </div>
                    
                    {priceListResult.detected_columns && (
                      <p className="text-xs text-slate-400" data-testid="pricelist-detected-columns">
                        Detected columns — Name: <span className="font-medium text-slate-600">{priceListResult.detected_columns.name || 'N/A'}</span>, 
                        Barcode: <span className="font-medium text-slate-600">{priceListResult.detected_columns.barcode || 'N/A'}</span>, 
                        Supplier Code: <span className="font-medium text-slate-600">{priceListResult.detected_columns.supplier_code || 'N/A'}</span>, 
                        Price: <span className="font-medium text-slate-600">{priceListResult.detected_columns.price || 'N/A'}</span>
                      </p>
                    )}

                    {/* Matched Products Table */}
                    {priceListResult.matches.length > 0 && (
                      <div className="border rounded-lg overflow-hidden">
                        <div className="max-h-[40vh] overflow-y-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-slate-50">
                                <TableHead className="w-8">
                                  <input 
                                    type="checkbox" 
                                    checked={priceListResult.matches.every(m => m.selected)}
                                    onChange={() => {
                                      const allSelected = priceListResult.matches.every(m => m.selected);
                                      setPriceListResult(prev => ({
                                        ...prev,
                                        matches: prev.matches.map(m => ({ ...m, selected: !allSelected }))
                                      }));
                                    }}
                                    className="rounded"
                                  />
                                </TableHead>
                                <TableHead className="text-xs">Product</TableHead>
                                <TableHead className="text-xs text-right">Old Buy</TableHead>
                                <TableHead className="text-xs text-right">New Buy</TableHead>
                                <TableHead className="text-xs text-right">Old Sell</TableHead>
                                <TableHead className="text-xs text-right">New Sell</TableHead>
                                <TableHead className="text-xs text-right">Markup</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {priceListResult.matches.map((match, idx) => (
                                <TableRow 
                                  key={match.product_id} 
                                  className={`${match.price_changed ? '' : 'opacity-50'} ${match.selected ? 'bg-amber-50/50' : ''}`}
                                >
                                  <TableCell>
                                    <input 
                                      type="checkbox" 
                                      checked={match.selected} 
                                      onChange={() => togglePriceItem(idx)}
                                      className="rounded"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <p className="text-sm font-medium truncate max-w-[200px]">{match.product_name}</p>
                                    <p className="text-xs text-slate-400">
                                      {match.match_method}
                                      {match.supplier_code ? <> · <span className="font-mono text-slate-500">SC: {match.supplier_code}</span></> : null}
                                    </p>
                                  </TableCell>
                                  <TableCell className="text-right text-sm">${match.old_buy_price.toFixed(2)}</TableCell>
                                  <TableCell className={`text-right text-sm font-medium ${match.new_buy_price > match.old_buy_price ? 'text-red-600' : match.new_buy_price < match.old_buy_price ? 'text-green-600' : ''}`}>
                                    ${match.new_buy_price.toFixed(2)}
                                  </TableCell>
                                  <TableCell className="text-right text-sm">${match.old_sell_price.toFixed(2)}</TableCell>
                                  <TableCell className="text-right text-sm font-medium">${match.new_sell_price.toFixed(2)}</TableCell>
                                  <TableCell className="text-right text-xs text-slate-500">{match.markup_pct}%</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}

                    {/* Unmatched Items */}
                    {priceListResult.unmatched_count > 0 && (
                      <details className="text-sm">
                        <summary className="cursor-pointer text-red-600 font-medium">
                          {priceListResult.unmatched_count} unmatched items (not in your inventory)
                        </summary>
                        <div className="mt-2 max-h-32 overflow-y-auto bg-red-50 rounded-lg p-2 space-y-1">
                          {priceListResult.unmatched.map((item, i) => (
                            <p key={i} className="text-xs text-red-700">
                              {item.name}
                              {item.barcode ? ` (${item.barcode})` : ''}
                              {item.supplier_code ? ` [SC: ${item.supplier_code}]` : ''}
                              {' — '}${item.price.toFixed(2)}
                            </p>
                          ))}
                        </div>
                      </details>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        onClick={() => { setPriceListFile(null); setPriceListResult(null); }}
                        className="flex-1"
                      >
                        Upload Different File
                      </Button>
                      <Button 
                        onClick={applyPriceUpdates}
                        disabled={priceListApplying || priceListResult.matches.filter(m => m.selected).length === 0}
                        className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                        data-testid="apply-prices-button"
                      >
                        {priceListApplying 
                          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Applying...</>
                          : <><CheckCircle className="w-4 h-4 mr-2" /> Update {priceListResult.matches.filter(m => m.selected).length} Products</>
                        }
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Product Button */}
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-orange-500 hover:bg-orange-600 text-white font-semibold uppercase tracking-wide"
                data-testid="add-product-button"
                onClick={() => resetForm()}
              >
                <Plus className="w-4 h-4 mr-2" /> Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl font-bold uppercase">Add New Product</DialogTitle>
                <DialogDescription className="text-slate-500">Enter the details for your new supplement product.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAdd} className="space-y-4">
                {/* Product Name */}
                <div className="space-y-2">
                  <Label htmlFor="add-name">Product Name *</Label>
                  <Input
                    id="add-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Whey Protein 2kg"
                    required
                    data-testid="add-product-name"
                  />
                </div>
                
                {/* Barcode & Supplier Code */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="add-barcode">Barcode</Label>
                    <div className="flex gap-2">
                      <Input
                        id="add-barcode"
                        value={formData.barcode}
                        onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                        placeholder="Scan or type the product's barcode"
                        disabled={generateBarcode}
                        data-testid="add-barcode-input"
                        className={generateBarcode ? 'bg-slate-100' : ''}
                      />
                    </div>
                    <label className="flex items-center gap-2 text-xs text-slate-600">
                      <input
                        type="checkbox"
                        checked={generateBarcode}
                        disabled={!!(formData.barcode || '').trim()}
                        onChange={(e) => setGenerateBarcode(e.target.checked)}
                        className="w-3 h-3"
                        data-testid="add-auto-generate-barcode-checkbox"
                      />
                      {(formData.barcode || '').trim() ? 'Scanned barcode will be used' : 'No barcode on the product — issue an in-store code'}
                    </label>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="add-supplier-code">Supplier Code</Label>
                    <Input
                      id="add-supplier-code"
                      value={formData.supplier_code}
                      onChange={(e) => setFormData({ ...formData, supplier_code: e.target.value })}
                      placeholder="e.g. ABC-12345"
                    />
                  </div>
                </div>
                
                {/* Brand & Supplier */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="add-brand">Brand</Label>
                    <Input
                      id="add-brand"
                      value={formData.brand}
                      onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                      placeholder="e.g. Optimum Nutrition"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="add-supplier">Supplier</Label>
                    <Select 
                      value={formData.supplier_id} 
                      onValueChange={(v) => {
                        const selectedSupplier = suppliers.find(s => s.id === v);
                        setFormData({ 
                          ...formData, 
                          supplier_id: v,
                          supplier_name: selectedSupplier?.name || ''
                        });
                      }}
                    >
                      <SelectTrigger data-testid="add-supplier-select">
                        <SelectValue placeholder="Select supplier" />
                      </SelectTrigger>
                      <SelectContent>
                        {suppliers.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Quantity & Category */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="add-quantity">Quantity</Label>
                    <Input
                      id="add-quantity"
                      type="number"
                      min="0"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })}
                      data-testid="add-product-quantity"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="add-category">Category</Label>
                    <Input
                      id="add-category"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      placeholder="e.g. Protein"
                    />
                  </div>
                </div>
                
                {/* Pricing Section */}
                <div className="border-t pt-4 mt-4">
                  <Label className="font-medium mb-3 block">Pricing *</Label>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="add-buy_price">Buy Price (Wholesale) *</Label>
                      <Input
                        id="add-buy_price"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.buy_price}
                        onChange={(e) => setFormData({ ...formData, buy_price: e.target.value })}
                        placeholder="0.00"
                        required
                        data-testid="add-product-buy-price"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="add-sell_price">Sell Price (Retail) *</Label>
                      <Input
                        id="add-sell_price"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.sell_price}
                        onChange={(e) => setFormData({ ...formData, sell_price: e.target.value })}
                        placeholder="0.00"
                        required
                        data-testid="add-product-sell-price"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="add-tax">Tax Rate (%)</Label>
                      <Input
                        id="add-tax"
                        type="number"
                        min="0"
                        step="0.1"
                        value={formData.tax_rate}
                        onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                  </div>
                  
                  {/* Margin & Markup Display — sell_price inc-GST, buy_price ex-GST */}
                  {parseFloat(formData.buy_price) > 0 && parseFloat(formData.sell_price) > 0 && (() => {
                    const buy = parseFloat(formData.buy_price);
                    const sell = parseFloat(formData.sell_price);
                    const sellExGst = sell / 1.15;
                    const margin = ((sellExGst - buy) / sellExGst * 100);
                    const markup = ((sellExGst - buy) / buy * 100);
                    const belowMin = margin < 40;
                    return (
                      <div className={`mt-3 p-3 rounded-lg border ${belowMin ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
                        <div className="flex gap-6 items-center">
                          <div>
                            <span className="text-xs text-slate-500">Margin:</span>
                            <span className={`ml-2 font-bold text-lg ${belowMin ? 'text-red-600' : 'text-emerald-600'}`}>
                              {margin.toFixed(1)}%
                            </span>
                          </div>
                          <div>
                            <span className="text-xs text-slate-500">Markup:</span>
                            <span className="ml-2 font-bold text-lg text-blue-600">
                              {markup.toFixed(1)}%
                            </span>
                          </div>
                          <div>
                            <span className="text-xs text-slate-500">Profit:</span>
                            <span className="ml-2 font-bold text-emerald-600">
                              ${(sell - buy).toFixed(2)}
                            </span>
                          </div>
                        </div>
                        {belowMin && !isAdmin && (
                          <p className="text-xs text-red-600 font-semibold mt-2 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> Below 40% minimum margin. Only admin can save this price.
                          </p>
                        )}
                        {belowMin && isAdmin && (
                          <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> Below 40% margin — admin override allowed.
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
                
                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="add-description">Description</Label>
                  <textarea
                    id="add-description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter product description, ingredients, usage instructions..."
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    rows={3}
                  />
                </div>
                
                {/* Product Image Section */}
                <div className="border-t pt-4 mt-4">
                  <Label className="font-medium mb-3 block">Product Image</Label>
                  <div className="flex gap-4">
                    <div className="w-24 h-24 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center overflow-hidden bg-slate-50">
                      {imagePreview ? (
                        <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                      ) : (
                        <ImageIcon className="w-8 h-8 text-slate-300" />
                      )}
                    </div>
                    <div className="flex-1 space-y-3">
                      <div>
                        <input
                          ref={imageInputRef}
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp,image/*"
                          onChange={handleImageSelect}
                          className="hidden"
                        />
                        <Button type="button" variant="outline" size="sm" onClick={() => imageInputRef.current?.click()}>
                          <Upload className="w-4 h-4 mr-2" /> Upload
                        </Button>
                        <span className="text-xs text-slate-500 ml-2">JPG, PNG, WEBP (up to 10MB — auto-resized)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">or</span>
                        <Input
                          placeholder="Enter image URL"
                          value={formData.image_url || ''}
                          onChange={(e) => handleImageUrlChange(e.target.value)}
                          className="flex-1 h-8 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Publish to Shopify */}
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={publishToShopify}
                      onChange={(e) => setPublishToShopify(e.target.checked)}
                      className="w-5 h-5 rounded border-green-400 text-green-600 focus:ring-green-500"
                    />
                    <div className="flex items-center gap-2">
                      <ShoppingBag className="w-5 h-5 text-green-600" />
                      <div>
                        <span className="font-medium text-green-800">Publish to Shopify</span>
                        <p className="text-xs text-green-600">Create this product on your Shopify store</p>
                      </div>
                    </div>
                  </label>
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={formLoading || publishingToShopify} className="bg-slate-900 hover:bg-slate-800" data-testid="add-product-submit">
                    {formLoading || publishingToShopify ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Product'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search Bar */}
      <Card className="border rounded-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search by name, barcode, brand or category... (shows stock for both stores)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-10"
              data-testid="inventory-search"
            />
            {/* Inline spinner — never unmount the input, never steal focus */}
            {loading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-700 rounded-full animate-spin" />
              </div>
            )}
          </div>
          {searchTerm && filteredProducts.length > 0 && (
            <p className="text-sm text-slate-500 mt-2">
              Found {filteredProducts.length} products matching "{searchTerm}" 
              - showing inventory for all stores
            </p>
          )}
        </CardContent>
      </Card>

      {/* Category Filter Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200" data-testid="category-filter-tabs">
        <button
          onClick={() => handleCategoryFilter('all')}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            categoryFilter === 'all' 
              ? 'border-slate-900 text-slate-900' 
              : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
          }`}
          data-testid="filter-tab-all"
        >
          All Products
        </button>
        <button
          onClick={() => handleCategoryFilter('wellness')}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px flex items-center gap-1.5 ${
            categoryFilter === 'wellness' 
              ? 'border-teal-600 text-teal-700' 
              : 'border-transparent text-slate-500 hover:text-teal-600 hover:border-teal-300'
          }`}
          data-testid="filter-tab-wellness"
        >
          <Globe className="w-3.5 h-3.5" /> Wellness Dispensary
        </button>
        <button
          onClick={() => handleCategoryFilter('nobarcode')}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px flex items-center gap-1.5 ${
            categoryFilter === 'nobarcode' 
              ? 'border-amber-600 text-amber-700' 
              : 'border-transparent text-slate-500 hover:text-amber-600 hover:border-amber-300'
          }`}
          data-testid="filter-tab-nobarcode"
        >
          <AlertTriangle className="w-3.5 h-3.5" /> No Barcode
          {categoryFilter === 'nobarcode' && !loading && (
            <Badge className="bg-amber-100 text-amber-800 ml-1 px-1.5 py-0 text-xs">{totalProducts}</Badge>
          )}
        </button>
      </div>

      {/* Bulk Actions Bar */}
      {selectedProducts.length > 0 && (
        <Card className="border-2 border-blue-400 bg-blue-50 rounded-sm">
          <CardContent className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Badge className="bg-blue-600 text-white px-3 py-1">
                {selectedProducts.length} selected
              </Badge>
              <span className="text-sm text-blue-700">
                Select products to bulk edit
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowBulkEditSupplier(true)}
                className="border-blue-400 text-blue-700 hover:bg-blue-100"
              >
                <Edit className="w-4 h-4 mr-1" /> Change Supplier
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const selected = filteredProducts.filter(p => selectedProducts.includes(p.id));
                  if (selected.length === 0) return;
                  setLabelDialogProducts(selected);
                }}
                className="border-green-400 text-green-700 hover:bg-green-100"
                data-testid="bulk-print-labels-btn"
              >
                <Tag className="w-4 h-4 mr-1" /> Print Labels
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={openPublishDialog}
                className="border-purple-400 text-purple-700 hover:bg-purple-100"
                data-testid="bulk-publish-shopify-btn"
              >
                <ShoppingBag className="w-4 h-4 mr-1" /> Publish to Shopify
              </Button>
              {/* External transfer to the owner's Royal Oak store — platform business only */}
              {user?.is_platform_business && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={openTransferDialog}
                className="border-orange-400 text-orange-700 hover:bg-orange-100"
                data-testid="transfer-royal-oak-btn"
              >
                <ArrowRightLeft className="w-4 h-4 mr-1" /> Transfer to Royal Oak
              </Button>
              )}
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleBulkDelete}
                className="border-red-400 text-red-700 hover:bg-red-100"
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete Selected
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setSelectedProducts([])}
                className="text-slate-500"
              >
                Clear Selection
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Shopify Bulk Publish Dialog */}
      <Dialog open={isPublishDialogOpen} onOpenChange={setIsPublishDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-purple-500" /> Publish to Shopify
            </DialogTitle>
            <DialogDescription>
              Review prices and choose a collection for {selectedProducts.length} selected product{selectedProducts.length !== 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>

          {publishResult ? (
            <div className="space-y-3 py-2">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="font-medium text-green-800">
                  Published {publishResult.published} product{publishResult.published !== 1 ? 's' : ''} to Shopify
                </p>
                {publishResult.skipped > 0 && (
                  <p className="text-sm text-green-600 mt-1">{publishResult.skipped} already on Shopify (skipped)</p>
                )}
                {publishResult.errors?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm text-red-600 font-medium">{publishResult.errors.length} error(s):</p>
                    {publishResult.errors.map((e, i) => (
                      <p key={i} className="text-xs text-red-500">{e.name || e.product_id}: {e.error}</p>
                    ))}
                  </div>
                )}
              </div>
              <Button variant="outline" className="w-full" onClick={() => { setIsPublishDialogOpen(false); setPublishResult(null); setSelectedProducts([]); }}>
                Close
              </Button>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {/* Collection Picker */}
              <div className="space-y-2">
                <Label className="font-medium">Shopify Collection (optional)</Label>
                {collectionsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading collections...</div>
                ) : (
                  <Select value={selectedCollection} onValueChange={setSelectedCollection}>
                    <SelectTrigger data-testid="shopify-collection-select">
                      <SelectValue placeholder="-- No collection (just publish) --" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- No collection --</SelectItem>
                      {shopifyCollections.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Products with editable prices */}
              <div className="space-y-1">
                <Label className="font-medium">Products &amp; Prices</Label>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs font-bold">Product</TableHead>
                        <TableHead className="text-xs font-bold text-right w-28">Cost</TableHead>
                        <TableHead className="text-xs font-bold text-right w-32">Shopify Price</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredProducts.filter(p => selectedProducts.includes(p.id)).map(p => (
                        <TableRow key={p.id}>
                          <TableCell className="py-2">
                            <p className="text-sm font-medium truncate max-w-[280px]">{p.name}</p>
                            <p className="text-xs text-slate-400">{p.brand}</p>
                          </TableCell>
                          <TableCell className="text-right text-sm text-slate-500">${(p.buy_price || 0).toFixed(2)}</TableCell>
                          <TableCell className="text-right py-1">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={publishPrices[p.id] ?? ''}
                              onChange={(e) => setPublishPrices(prev => ({ ...prev, [p.id]: e.target.value }))}
                              className="w-28 text-right h-8 text-sm ml-auto"
                              data-testid={`publish-price-${p.id}`}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <DialogFooter className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setIsPublishDialogOpen(false)}>Cancel</Button>
                <Button
                  onClick={handleBulkPublish}
                  disabled={publishing}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="publish-confirm-btn"
                >
                  {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                  Publish {selectedProducts.length} to Shopify
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Transfer to Royal Oak Dialog */}
      <Dialog open={isTransferDialogOpen} onOpenChange={setIsTransferDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-orange-500" /> Transfer to Royal Oak
            </DialogTitle>
            <DialogDescription>
              Transfer {selectedProducts.length} product{selectedProducts.length !== 1 ? 's' : ''} from {selectedStore?.charAt(0).toUpperCase() + selectedStore?.slice(1)} to Health2000 Royal Oak. Stock will be deducted.
            </DialogDescription>
          </DialogHeader>

          {transferResult ? (
            <div className="space-y-3 py-2">
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="font-medium text-orange-800">
                  Transferred {transferResult.transferred} product{transferResult.transferred !== 1 ? 's' : ''} to Royal Oak
                </p>
                <p className="text-sm text-orange-600 mt-1">Transfer #{transferResult.transfer_id} — Invoice downloaded</p>
                {transferResult.errors?.length > 0 && (
                  <div className="mt-2">
                    {transferResult.errors.map((e, i) => (
                      <p key={i} className="text-xs text-red-500">{e.name || e.product_id}: {e.error}</p>
                    ))}
                  </div>
                )}
              </div>
              <Button variant="outline" className="w-full" onClick={() => { setIsTransferDialogOpen(false); setTransferResult(null); setSelectedProducts([]); }}>
                Close
              </Button>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm text-orange-700">
                  Set the quantity to transfer for each product. A PDF invoice will be generated automatically.
                </p>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs font-bold">Product</TableHead>
                      <TableHead className="text-xs font-bold text-center w-24">In Stock</TableHead>
                      <TableHead className="text-xs font-bold text-center w-28">Transfer Qty</TableHead>
                      <TableHead className="text-xs font-bold text-right w-24">Price</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProducts.filter(p => selectedProducts.includes(p.id)).map(p => {
                      const storeStock = selectedStore === 'eastridge' ? (p.stock_eastridge || 0) : (p.stock_orewa || 0);
                      return (
                        <TableRow key={p.id}>
                          <TableCell className="py-2">
                            <p className="text-sm font-medium truncate max-w-[260px]">{p.name}</p>
                            <p className="text-xs text-slate-400">{p.sku || p.barcode || ''}</p>
                          </TableCell>
                          <TableCell className="text-center text-sm">
                            <span className={storeStock < 0 ? 'text-red-600 font-bold' : storeStock === 0 ? 'text-amber-600 font-bold' : ''}>
                              {storeStock}
                            </span>
                          </TableCell>
                          <TableCell className="text-center py-1">
                            <Input
                              type="number"
                              min="0"
                              value={transferQuantities[p.id] ?? ''}
                              onChange={(e) => setTransferQuantities(prev => ({ ...prev, [p.id]: Number(e.target.value) }))}
                              className={`w-20 text-center h-8 text-sm mx-auto ${(transferQuantities[p.id] || 0) > storeStock ? 'border-amber-500 bg-amber-50' : ''}`}
                              data-testid={`transfer-qty-${p.id}`}
                              title={(transferQuantities[p.id] || 0) > storeStock ? `Source will go negative (${storeStock} → ${storeStock - (transferQuantities[p.id] || 0)}). That's OK — fix it later.` : ''}
                            />
                            {(transferQuantities[p.id] || 0) > storeStock && (
                              <p className="text-[10px] text-amber-700 mt-0.5">
                                source → {storeStock - (transferQuantities[p.id] || 0)}
                              </p>
                            )}
                          </TableCell>
                          <TableCell className="text-right text-sm">${(p.sell_price || 0).toFixed(2)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <DialogFooter className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setIsTransferDialogOpen(false)}>Cancel</Button>
                <Button
                  onClick={handleTransfer}
                  disabled={transferring}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                  data-testid="transfer-confirm-btn"
                >
                  {transferring ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRightLeft className="w-4 h-4 mr-2" />}
                  Transfer & Generate Invoice
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Products Table */}
      <Card className="border rounded-sm overflow-hidden" data-testid="products-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-10">
                <input
                  type="checkbox"
                  checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                  onChange={handleSelectAll}
                  className="w-4 h-4 rounded border-slate-300"
                  data-testid="select-all-checkbox"
                />
              </TableHead>
              <TableHead className="font-heading font-bold uppercase text-xs tracking-wider text-slate-600">Product</TableHead>
              <TableHead className="font-heading font-bold uppercase text-xs tracking-wider text-slate-600">Brand</TableHead>
              <TableHead className="font-heading font-bold uppercase text-xs tracking-wider text-slate-600 text-center">{storeName('orewa')}</TableHead>
              <TableHead className="font-heading font-bold uppercase text-xs tracking-wider text-slate-600 text-center">{storeName('eastridge')}</TableHead>
              <TableHead className="font-heading font-bold uppercase text-xs tracking-wider text-slate-600 text-center">Total</TableHead>
              <TableHead className="font-heading font-bold uppercase text-xs tracking-wider text-slate-600 text-right">Cost</TableHead>
              <TableHead className="font-heading font-bold uppercase text-xs tracking-wider text-slate-600 text-right">Retail</TableHead>
              <TableHead className="font-heading font-bold uppercase text-xs tracking-wider text-slate-600 text-right">Margin</TableHead>
              <TableHead className="font-heading font-bold uppercase text-xs tracking-wider text-slate-600 text-right">Markup</TableHead>
              <TableHead className="font-heading font-bold uppercase text-xs tracking-wider text-slate-600 text-right">Profit $</TableHead>
              <TableHead className="font-heading font-bold uppercase text-xs tracking-wider text-slate-600">Status</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredProducts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13} className="text-center py-12 text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  {searchTerm ? 'No products match your search' : 'No products yet. Add your first product!'}
                </TableCell>
              </TableRow>
            ) : (
              filteredProducts.map((product) => {
                const status = getStockStatus(product.quantity);
                return (
                  <TableRow 
                    key={product.id} 
                    className={`table-row-hover ${selectedProducts.includes(product.id) ? 'bg-blue-50' : ''}`} 
                    data-testid={`product-row-${product.id}`}
                  >
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedProducts.includes(product.id)}
                        onChange={() => toggleProductSelection(product.id)}
                        className="w-4 h-4 rounded border-slate-300"
                        data-testid={`select-product-${product.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">{product.name}</p>
                        <p className="text-xs text-slate-400">{product.barcode || product.sku || '-'}</p>
                        {product.supplier_name && <p className="text-xs text-blue-500 font-medium">{product.supplier_name}</p>}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm">{product.brand || '-'}</TableCell>
                    <TableCell className="text-center">
                      <span className="font-semibold text-blue-600">{product.stock_orewa || 0}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="font-semibold text-emerald-600">{product.stock_eastridge || 0}</span>
                    </TableCell>
                    <TableCell className="text-center font-bold">{product.quantity}</TableCell>
                    <TableCell className="text-right text-slate-600">${product.buy_price.toFixed(2)}</TableCell>
                    <TableCell className="text-right font-medium text-slate-900">${product.sell_price.toFixed(2)}</TableCell>
                    <TableCell className="text-right">
                      {(() => {
                        // sell_price is inc-GST, buy_price ex-GST — strip GST before computing margin
                        const sellExGst = product.sell_price > 0 ? product.sell_price / 1.15 : 0;
                        const margin = sellExGst > 0
                          ? ((sellExGst - product.buy_price) / sellExGst * 100)
                          : 0;
                        const isLowMargin = margin < 40;
                        return (
                          <span className={`font-medium ${isLowMargin ? 'text-red-500' : 'text-emerald-600'}`}>
                            {margin.toFixed(0)}%
                            {isLowMargin && <AlertTriangle className="w-3 h-3 inline ml-1" />}
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right">
                      {(() => {
                        // Markup also computed on ex-GST sell price
                        const sellExGst = product.sell_price > 0 ? product.sell_price / 1.15 : 0;
                        const markup = product.buy_price > 0
                          ? ((sellExGst - product.buy_price) / product.buy_price * 100)
                          : 0;
                        return (
                          <span className="font-medium text-slate-700">
                            {markup.toFixed(0)}%
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-medium text-emerald-600">
                        ${((product.sell_price || 0) - (product.buy_price || 0)).toFixed(2)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`status-badge ${status.class}`}>{status.label}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                          onClick={() => openEditDialog(product)}
                          data-testid={`quick-edit-${product.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`product-actions-${product.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(product)} data-testid={`edit-product-${product.id}`}>
                              <Edit className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => setLabelDialogProducts([product])}
                              data-testid={`print-label-${product.id}`}
                            >
                              <Tag className="w-4 h-4 mr-2" /> Print Labels ({selectedStore === 'eastridge' ? (product.stock_eastridge ?? 0) : (product.stock_orewa ?? 0)} in stock)
                            </DropdownMenuItem>
                            {!product.barcode && (
                              <DropdownMenuItem 
                                onClick={() => handleGenerateBarcode(product)}
                                data-testid={`generate-barcode-${product.id}`}
                              >
                                <Barcode className="w-4 h-4 mr-2" /> Generate Barcode
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              onClick={() => handleDelete(product.id)} 
                              className="text-red-600"
                              data-testid={`delete-product-${product.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        
        {/* Pagination Controls */}
        {totalProducts > pageSize && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50">
            <div className="text-sm text-slate-600">
              Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, totalProducts)} of {totalProducts} products
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchData(page - 1, searchTerm.length >= 2 ? searchTerm : '', categoryFilter === 'wellness' ? 'Wellness Dispensary' : categoryFilter === 'nobarcode' ? 'nobarcode' : '')}
                disabled={page === 0 || loading}
              >
                Previous
              </Button>
              <span className="text-sm text-slate-600">
                Page {page + 1} of {Math.ceil(totalProducts / pageSize)}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchData(page + 1, searchTerm.length >= 2 ? searchTerm : '', categoryFilter === 'wellness' ? 'Wellness Dispensary' : categoryFilter === 'nobarcode' ? 'nobarcode' : '')}
                disabled={(page + 1) * pageSize >= totalProducts || loading}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase">Edit Product</DialogTitle>
            <DialogDescription className="text-slate-500">Update the product information below.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Product Name *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. Whey Protein 2kg"
                required
                data-testid="edit-product-name"
              />
            </div>

            {/* Barcode & Supplier Code */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-barcode">Barcode</Label>
                <div className="flex gap-2">
                  <Input
                    id="edit-barcode"
                    className="font-mono min-w-[150px]"
                    value={formData.barcode || ''}
                    onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                    onBlur={(e) => {
                      // Typing a new primary barcode by hand: keep the previous one as an additional barcode
                      const prevCode = (selectedProduct?.barcode || '').trim();
                      const now = e.target.value.trim();
                      if (prevCode && now && now !== prevCode && !(formData.alt_barcodes || []).includes(prevCode)) {
                        setFormData(prev => ({ ...prev, alt_barcodes: [...(prev.alt_barcodes || []), prevCode] }));
                        toast.info(`${prevCode} kept as an additional barcode — remove the chip if the old code should stop scanning`);
                      }
                    }}
                    placeholder="Custom barcode"
                    data-testid="edit-product-barcode"
                  />
                  {!(formData.barcode || '').trim() && !(formData.alt_barcodes || []).length && (
                  <Button
                    type="button"
                    variant="outline"
                    className="shrink-0 border-purple-300 text-purple-700 hover:bg-purple-50"
                    title="This product has no barcode yet — use its numeric SKU or issue one in-store code"
                    data-testid="edit-generate-barcode-btn"
                    onClick={async () => {
                      try {
                        const res = await axios.post(`${API_URL}/products/${selectedProduct.id}/generate-barcode`, {}, { headers: getAuthHeader() });
                        setFormData(prev => ({ ...prev, barcode: res.data.barcode }));
                        toast.success(res.data.source === 'sku' ? `Using the SKU ${res.data.barcode} as the barcode` : `In-store barcode issued: ${res.data.barcode}`);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to generate barcode');
                      }
                    }}
                  >
                    <Barcode className="w-4 h-4 mr-1" /> Generate
                  </Button>
                  )}
                </div>
                {/* Additional barcodes — every one of these scans at the POS */}
                <div className="flex flex-wrap items-center gap-1.5 pt-1" data-testid="edit-alt-barcodes">
                  {(formData.alt_barcodes || []).map(code => (
                    <span key={code} className="inline-flex items-center gap-1 text-xs bg-slate-100 border border-slate-200 rounded-full pl-2.5 pr-1 py-0.5 font-mono">
                      {code}
                      <button type="button" className="text-slate-400 hover:text-red-600 px-1" title="Remove this barcode"
                        onClick={() => setFormData(prev => ({ ...prev, alt_barcodes: (prev.alt_barcodes || []).filter(c => c !== code) }))}
                        data-testid={`remove-alt-barcode-${code}`}>×</button>
                    </span>
                  ))}
                  <input
                    className="text-xs border border-dashed border-slate-300 rounded-full px-2.5 py-0.5 w-56 focus:outline-none focus:border-purple-400"
                    placeholder="+ another barcode, then Enter"
                    data-testid="add-alt-barcode-input"
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return;
                      e.preventDefault();
                      const code = e.target.value.trim();
                      if (code.length < 4) return;
                      if (code === (formData.barcode || '').trim() || (formData.alt_barcodes || []).includes(code)) { e.target.value = ''; return; }
                      setFormData(prev => ({ ...prev, alt_barcodes: [...(prev.alt_barcodes || []), code] }));
                      e.target.value = '';
                    }}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-supplier-code">Supplier Code</Label>
                <Input
                  id="edit-supplier-code"
                  value={formData.supplier_code || ''}
                  onChange={(e) => setFormData({ ...formData, supplier_code: e.target.value })}
                  placeholder="e.g. ABC-12345"
                  data-testid="edit-product-supplier-code"
                />
              </div>
            </div>

            {/* Brand & Supplier */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-brand">Brand</Label>
                <Input
                  id="edit-brand"
                  value={formData.brand || ''}
                  onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                  placeholder="e.g. Optimum Nutrition"
                  data-testid="edit-product-brand"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-supplier">Supplier</Label>
                <Select
                  value={formData.supplier_id || ''}
                  onValueChange={(v) => {
                    const selectedSupplier = suppliers.find(s => s.id === v);
                    setFormData({
                      ...formData,
                      supplier_id: v,
                      supplier_name: selectedSupplier?.name || ''
                    });
                  }}
                >
                  <SelectTrigger data-testid="edit-supplier-select">
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-stock-orewa">Stock {storeName('orewa')}</Label>
                <Input
                  id="edit-stock-orewa"
                  type="number"
                  value={formData.stock_orewa ?? 0}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    setFormData({ ...formData, stock_orewa: val, quantity: val + (formData.stock_eastridge || 0) });
                  }}
                  data-testid="edit-stock-orewa"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-stock-eastridge">Stock {storeName('eastridge')}</Label>
                <Input
                  id="edit-stock-eastridge"
                  type="number"
                  value={formData.stock_eastridge ?? 0}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    setFormData({ ...formData, stock_eastridge: val, quantity: (formData.stock_orewa || 0) + val });
                  }}
                  data-testid="edit-stock-eastridge"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-category">Category</Label>
                <Input
                  id="edit-category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="e.g. Protein"
                  data-testid="edit-product-category"
                />
              </div>
            </div>
            {/* Pricing Section */}
            <div className="border-t pt-4 mt-4">
              <Label className="font-medium mb-3 block">Pricing *</Label>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-buy_price">Buy Price (Wholesale) *</Label>
                  <Input
                    id="edit-buy_price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.buy_price}
                    onChange={(e) => setFormData({ ...formData, buy_price: parseFloat(e.target.value) || 0 })}
                    required
                    data-testid="edit-product-buy-price"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-sell_price">Sell Price (Retail) *</Label>
                  <Input
                    id="edit-sell_price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.sell_price}
                    onChange={(e) => setFormData({ ...formData, sell_price: parseFloat(e.target.value) || 0 })}
                    required
                    data-testid="edit-product-sell-price"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-tax">Tax Rate (%)</Label>
                  <Input
                    id="edit-tax"
                    type="number"
                    min="0"
                    step="0.1"
                    value={formData.tax_rate ?? 15}
                    onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })}
                    data-testid="edit-product-tax-rate"
                  />
                </div>
              </div>
            </div>
            
            {/* Margin & Markup Preview for Edit — sell inc-GST, buy ex-GST */}
            {parseFloat(formData.buy_price) > 0 && parseFloat(formData.sell_price) > 0 && (() => {
              const buy = parseFloat(formData.buy_price);
              const sell = parseFloat(formData.sell_price);
              const sellExGst = sell / 1.15;
              const margin = ((sellExGst - buy) / sellExGst * 100);
              const markup = ((sellExGst - buy) / buy * 100);
              const belowMin = margin < 40;
              return (
                <div className={`p-3 rounded-lg border ${belowMin ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
                  <div className="flex gap-6 items-center">
                    <div>
                      <span className="text-xs text-slate-500">Margin:</span>
                      <span className={`ml-2 font-bold text-lg ${belowMin ? 'text-red-600' : 'text-emerald-600'}`}>
                        {margin.toFixed(1)}%
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-slate-500">Markup:</span>
                      <span className="ml-2 font-bold text-lg text-blue-600">
                        {markup.toFixed(1)}%
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-slate-500">Profit:</span>
                      <span className="ml-2 font-bold text-emerald-600">
                        ${(sell - buy).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  {belowMin && !isAdmin && (
                    <p className="text-xs text-red-600 font-semibold mt-2 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Below 40% minimum margin. Only admin can save this price.
                    </p>
                  )}
                  {belowMin && isAdmin && (
                    <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Below 40% margin — admin override allowed.
                    </p>
                  )}
                </div>
              );
            })()}

            {/* Description Field */}
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <textarea
                id="edit-description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Product description..."
                className="w-full min-h-[80px] px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400"
                data-testid="edit-product-description"
              />
            </div>
            
            {/* Product Image Section */}
            <div className="border-t pt-4 mt-4">
              <Label className="font-medium mb-3 block">Product Image</Label>
              <div className="flex gap-4">
                <div className="w-24 h-24 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center overflow-hidden bg-slate-50">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="w-8 h-8 text-slate-300" />
                  )}
                </div>
                <div className="flex-1 space-y-3">
                  <div>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                    <Button type="button" variant="outline" size="sm" onClick={() => imageInputRef.current?.click()}>
                      <Upload className="w-4 h-4 mr-2" /> Upload
                    </Button>
                    <span className="text-xs text-slate-500 ml-2">JPG, PNG, WEBP (up to 10MB — auto-resized)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">or</span>
                    <Input
                      placeholder="Enter image URL"
                      value={formData.image_url || ''}
                      onChange={(e) => handleImageUrlChange(e.target.value)}
                      className="flex-1 h-8 text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Publish to Shopify */}
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={publishToShopify}
                  onChange={(e) => setPublishToShopify(e.target.checked)}
                  className="w-5 h-5 rounded border-green-400 text-green-600 focus:ring-green-500"
                  data-testid="edit-publish-to-shopify"
                />
                <div className="flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5 text-green-600" />
                  <div>
                    <span className="font-medium text-green-800">Publish to Shopify</span>
                    <p className="text-xs text-green-600">
                      {selectedProduct?.shopify_product_id
                        ? 'Already on Shopify — tick to re-sync this product'
                        : 'Push this product to your Shopify store on save'}
                    </p>
                  </div>
                </div>
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading || publishingToShopify} className="bg-slate-900 hover:bg-slate-800" data-testid="edit-product-submit">
                {formLoading || publishingToShopify ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Stock Count Dialog */}
      <Dialog open={isStockCountOpen} onOpenChange={setIsStockCountOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-blue-500" /> Stock Count
            </DialogTitle>
            <DialogDescription>
              Enter the actual counted quantities to reconcile inventory discrepancies
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Search and Summary */}
            <div className="flex items-center justify-between gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search products..."
                  value={stockCountSearch}
                  onChange={(e) => setStockCountSearch(e.target.value)}
                  className="pl-10"
                  data-testid="stock-count-search"
                />
              </div>
              <Select value={stockCountBrand} onValueChange={setStockCountBrand}>
                <SelectTrigger className="w-48" data-testid="stock-count-brand-filter">
                  <SelectValue placeholder="Filter by brand..." />
                </SelectTrigger>
                <SelectContent className="max-h-80 overflow-y-auto">
                  <SelectItem value="all">All brands</SelectItem>
                  {stockCountBrands.map(b => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={stockCountSupplier} onValueChange={setStockCountSupplier}>
                <SelectTrigger className="w-48" data-testid="stock-count-supplier-filter">
                  <SelectValue placeholder="Filter by supplier..." />
                </SelectTrigger>
                <SelectContent className="max-h-80 overflow-y-auto">
                  <SelectItem value="all">All suppliers</SelectItem>
                  {stockCountSuppliers.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={handlePrintStockCountSheet}
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                data-testid="print-stock-count-sheet"
              >
                <Printer className="w-4 h-4 mr-1" /> Print Sheet
              </Button>
              {hasDiscrepancies() && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  {getDiscrepancyCount()} discrepancies found
                </Badge>
              )}
            </div>
            
            {/* Stock Count Table */}
            <ScrollArea className="h-[400px] border rounded-sm">
              <Table>
                <TableHeader className="sticky top-0 bg-slate-50 z-10">
                  <TableRow>
                    <TableHead className="font-heading font-bold uppercase text-xs">Product</TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-center w-24">
                      <div className="text-blue-600">{storeName('orewa')}</div>
                      <div className="text-[10px] text-slate-400 font-normal">System</div>
                    </TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-center w-28">
                      <div className="text-blue-600">{storeName('orewa')}</div>
                      <div className="text-[10px] text-slate-400 font-normal">Counted</div>
                    </TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-center w-24">
                      <div className="text-emerald-600">{storeName('eastridge')}</div>
                      <div className="text-[10px] text-slate-400 font-normal">System</div>
                    </TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-center w-28">
                      <div className="text-emerald-600">{storeName('eastridge')}</div>
                      <div className="text-[10px] text-slate-400 font-normal">Counted</div>
                    </TableHead>
                    <TableHead className="font-heading font-bold uppercase text-xs text-center w-20">Diff</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStockCountItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No products found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredStockCountItems.map(item => {
                      const diffOrewa = getStockDifference(item, 'orewa');
                      const diffEastridge = getStockDifference(item, 'eastridge');
                      const hasIssue = (diffOrewa !== null && diffOrewa !== 0) || (diffEastridge !== null && diffEastridge !== 0);
                      
                      return (
                        <TableRow 
                          key={item.id} 
                          className={hasIssue ? 'bg-amber-50' : ''}
                          data-testid={`stock-count-row-${item.id}`}
                        >
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm">{item.name}</p>
                              <p className="text-xs text-slate-400">{item.barcode || item.brand || '-'}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="font-semibold text-blue-600">{item.system_orewa}</span>
                          </TableCell>
                          <TableCell className="text-center">
                            <Input
                              type="number"
                              min="0"
                              className="w-20 mx-auto text-center h-8"
                              placeholder="-"
                              value={item.counted_orewa ?? ''}
                              onChange={(e) => updateCountedStock(item.id, 'orewa', e.target.value)}
                              data-testid={`count-orewa-${item.id}`}
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="font-semibold text-emerald-600">{item.system_eastridge}</span>
                          </TableCell>
                          <TableCell className="text-center">
                            <Input
                              type="number"
                              min="0"
                              className="w-20 mx-auto text-center h-8"
                              placeholder="-"
                              value={item.counted_eastridge ?? ''}
                              onChange={(e) => updateCountedStock(item.id, 'eastridge', e.target.value)}
                              data-testid={`count-eastridge-${item.id}`}
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex flex-col gap-1">
                              {diffOrewa !== null && diffOrewa !== 0 && (
                                <Badge 
                                  variant="outline" 
                                  className={`text-xs ${diffOrewa > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}
                                >
                                  O: {diffOrewa > 0 ? '+' : ''}{diffOrewa}
                                </Badge>
                              )}
                              {diffEastridge !== null && diffEastridge !== 0 && (
                                <Badge 
                                  variant="outline" 
                                  className={`text-xs ${diffEastridge > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}
                                >
                                  E: {diffEastridge > 0 ? '+' : ''}{diffEastridge}
                                </Badge>
                              )}
                              {diffOrewa === 0 && diffEastridge === 0 && item.touched && (
                                <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" />
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
            
            {/* Actions */}
            <div className="flex justify-between items-center pt-2">
              <p className="text-sm text-slate-500">
                {filteredStockCountItems.length} products • Enter counted values to compare with system stock
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => setIsStockCountOpen(false)}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveStockCount}
                  disabled={stockCountLoading || !hasDiscrepancies()}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-stock-count"
                >
                  {stockCountLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save {getDiscrepancyCount()} Changes
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Supplier Dialog */}
      <Dialog open={showBulkEditSupplier} onOpenChange={setShowBulkEditSupplier}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase">
              Change Supplier
            </DialogTitle>
            <DialogDescription>
              Update supplier for {selectedProducts.length} selected product(s)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Supplier Name</Label>
              <Select value={bulkSupplierName} onValueChange={setBulkSupplierName}>
                <SelectTrigger data-testid="bulk-supplier-select">
                  <SelectValue placeholder="Select a supplier..." />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map(supplier => (
                    <SelectItem key={supplier.id} value={supplier.name}>
                      {supplier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Or type a new supplier name:</p>
              <Input
                value={bulkSupplierName}
                onChange={(e) => setBulkSupplierName(e.target.value)}
                placeholder="Enter supplier name..."
                data-testid="bulk-supplier-input"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowBulkEditSupplier(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleBulkUpdateSupplier}
              disabled={bulkUpdating || !bulkSupplierName.trim()}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="bulk-update-supplier-btn"
            >
              {bulkUpdating ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Updating...</>
              ) : (
                <>Update {selectedProducts.length} Products</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inventory;
